/**
 * Глобальный перехватчик для axios
 * 
 * Отвечает за:
 * - Добавление токена авторизации ко всем запросам
 * - Перехват ошибок авторизации и попытку обновления токена
 * - Перенаправление на страницу входа при необходимости
 */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getCookie } from './cookieUtils';
import { tokenManager } from './tokenManager';

/**
 * Настраивает глобальные перехватчики для всех экземпляров axios
 */
export const setupAxiosInterceptors = () => {
    // Перехватчик запросов для добавления токена авторизации
    axios.interceptors.request.use(
        (config) => {
            const token = getCookie('access_token');
            if (token) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Маркер для предотвращения циклического обновления токена
    let isRefreshing = false;
    // Очередь запросов ожидающих обновления токена
    let failedQueue: Array<{
        resolve: (value: unknown) => void;
        reject: (reason?: any) => void;
        config: AxiosRequestConfig;
    }> = [];

    // Обработка очереди запросов
    const processQueue = (error: any = null) => {
        failedQueue.forEach(promise => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(axios(promise.config));
            }
        });

        failedQueue = [];
    };

    // Перехватчик ответов для обработки ошибок авторизации
    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

            // Обрабатываем только 401 ошибки и только если запрос еще не пытались повторить
            if (error.response?.status === 401 && !originalRequest._retry) {
                if (isRefreshing) {
                    // Если токен уже обновляется, добавляем запрос в очередь
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject, config: originalRequest });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    // Пытаемся обновить токен через менеджер
                    const refreshSuccessful = await tokenManager.forceRefreshToken();

                    if (refreshSuccessful) {
                        // Токен успешно обновлен, повторяем исходный запрос
                        const token = getCookie('access_token');
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }

                        // Выполняем все запросы из очереди
                        processQueue();
                        isRefreshing = false;

                        // Повторяем исходный запрос с новым токеном
                        return axios(originalRequest);
                    } else {
                        // Обновление токена не удалось
                        processQueue(error);
                        isRefreshing = false;

                        // Отправляем событие о проблемах с авторизацией для редиректа на логин
                        const event = new Event('auth:unauthorized');
                        window.dispatchEvent(event);

                        return Promise.reject(error);
                    }
                } catch (refreshError) {
                    processQueue(refreshError);
                    isRefreshing = false;

                    // Отправляем событие о проблемах с авторизацией для редиректа на логин
                    const event = new Event('auth:unauthorized');
                    window.dispatchEvent(event);

                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );
}; 